import { DirectionsBike, DirectionsRun, Schedule, TrendingUp } from '@mui/icons-material';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import React from 'react';
import type { WeekSummary } from '../../hooks/useWeekSummary';
import { formatDistance } from '../../utils/dateUtils';
import NoteInfoModal from '../common/NoteInfoModal';

interface WeekSummaryCardProps {
  weekSummary: WeekSummary;
}

export const WeekSummaryCard: React.FC<WeekSummaryCardProps> = ({ weekSummary }) => {
  const hasRunning = weekSummary.runningDistance > 0;
  const hasCycling = weekSummary.cyclingDistance > 0;

  return (
    <Card
      elevation={2}
      sx={{
        mb: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <CardContent
        sx={{
          p: { xs: 0.5, sm: 0.3 },
          m: { sm: 0.3 },
          '&:last-child': { pb: { xs: 0.5, sm: 0.3 } },
        }}
      >
        <Grid container spacing={0} alignItems="center">
          <Grid size={{ xs: 0, sm: 2 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Box display="flex" alignItems="center">
              <Typography
                variant="subtitle2"
                color="text.primary"
                fontSize="0.9rem"
                fontWeight="600"
                sx={{
                  textAlign: { xs: 'center', sm: 'left' },
                  color: 'text.secondary',
                }}
              >
                📊 Week Summary
              </Typography>
              {/* AI Coach week note icon */}
              {weekSummary.weekNote && (
                <NoteInfoModal
                  title={`Week ${weekSummary.weekNumber || ''} - AI Coach Note`}
                  note={weekSummary.weekNote}
                  iconSize="small"
                />
              )}
            </Box>
          </Grid>

          {/* Running Distance */}
          <Grid
            size={{
              xs: hasRunning && hasCycling ? 3 : 4,
              sm: hasRunning && hasCycling ? 2.5 : 2.5,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent={{ xs: 'center', sm: 'flex-start' }}
              sx={{
                bgcolor: 'transparent',
                borderRadius: 2,
                p: 0.5,
                border: '1px solid #1976d2',
                borderColor: 'primary.main',
              }}
            >
              <DirectionsRun sx={{ mr: 0.5, fontSize: '1.2rem', color: 'primary.main' }} />
              <Box>
                <Typography variant="body2" fontSize="0.7rem" color="primary.main" fontWeight="500">
                  Run
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontSize="0.95rem"
                  fontWeight="700"
                  color="primary.main"
                >
                  {formatDistance(weekSummary.runningDistance)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Cycling Distance - only show if there's cycling */}
          {hasCycling && (
            <Grid size={{ xs: 3, sm: 2.5 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent={{ xs: 'center', sm: 'flex-start' }}
                sx={{
                  bgcolor: 'transparent',
                  borderRadius: 2,
                  p: 0.5,
                  border: '1px solid #9c27b0',
                  borderColor: 'secondary.main',
                }}
              >
                <DirectionsBike sx={{ mr: 0.5, fontSize: '1.2rem', color: 'secondary.main' }} />
                <Box>
                  <Typography
                    variant="body2"
                    fontSize="0.7rem"
                    color="secondary.main"
                    fontWeight="500"
                  >
                    Ride
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontSize="0.95rem"
                    fontWeight="700"
                    color="secondary.main"
                  >
                    {formatDistance(weekSummary.cyclingDistance)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          <Grid size={{ xs: hasRunning && hasCycling ? 3 : 4, sm: 2.5 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent={{ xs: 'center', sm: 'flex-start' }}
              sx={{
                bgcolor: 'transparent',
                borderRadius: 2,
                p: 0.5,
                border: '1px solid #4caf50',
                borderColor: 'success.main',
              }}
            >
              <Schedule sx={{ mr: 0.5, fontSize: '1.2rem', color: 'success.main' }} />
              <Box>
                <Typography variant="body2" fontSize="0.7rem" color="success.main" fontWeight="500">
                  Workouts
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontSize="0.95rem"
                  fontWeight="700"
                  color="success.main"
                >
                  {weekSummary.workoutCount}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: hasRunning && hasCycling ? 3 : 4, sm: 2.5 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent={{ xs: 'center', sm: 'flex-start' }}
              sx={{
                bgcolor: 'transparent',
                borderRadius: 2,
                p: 0.5,
                border: '1px solid #ff9800',
                borderColor: 'warning.main',
              }}
            >
              <TrendingUp sx={{ mr: 0.5, fontSize: '1.2rem', color: 'warning.main' }} />
              <Box>
                <Typography variant="body2" fontSize="0.7rem" color="warning.main" fontWeight="500">
                  Load
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontSize="0.95rem"
                  fontWeight="700"
                  color="warning.main"
                >
                  {Math.round(weekSummary.totalLoad)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
